import React from 'react';
import {FormattedMessage} from 'react-intl';
import CalendarSvg from '../../assets/icons/calendar.svg';
import MarkerPin01Svg from '../../assets/icons0/marker-pin-01.svg';
import {ErrorBoundary} from 'react-error-boundary';
import i18n from '../../i18n';
import SuspenseImageStaticSize from '../SuspenseImageStaticSize';
import {Link, useLocation} from 'react-router-dom';
import useQuery from '../../useQuery';
import {RouterLinkButton} from '../button/button';
import ArrowUpRightSvg from '../../assets/icons/arrow-up-right.svg';
import Browser from '../Browser';
import ErrorFallback from '../ErrorFallback';
import getServiceUrl from '../../utilities/get-service-url';
import getTagValue, {getPosterUrl, getServiceTagValue} from '../../utilities/get-tag-value';
import {format} from 'date-fns';
import {DEFAULT_SERVICE_DESCRIPTION_SEPARATOR} from '../../constants';
import './event-card.scss';
import i18ns from '../../i18ns';
import Focus from './focus';

const EventCard = React.memo(({service, mode = 'default', highlightResult, snippetResult, highlightCover = false}) => {
    const location = useLocation();
    const {locale = 'uk'} = useQuery();
    const cover = React.useMemo(() => {
        const posterUrl = getPosterUrl(service.description);
        if (posterUrl) {
            return posterUrl;
        } else if (i18n(service, 'details', locale)) {
            const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png)/i;
            const found = i18n(service, 'details', locale).match(regex);
            return found && found[0];
        }
    }, [service, locale]);
    const serviceUrl = getServiceUrl({description: service.description, id: service.id, slug: service.slug, warp: service.warp, location});
    const locationName = i18n(service, 'location', locale) || decodeURI(getServiceTagValue(service.description, 'locationName', {defaultValue: ''}));
    const target = service.target ? format(new Date(service.target), 'dd.MM.yyyy') : null;
    const tags = service.description ? service.description.split(DEFAULT_SERVICE_DESCRIPTION_SEPARATOR) : [];
    const targetEndVal = getTagValue(tags, 'targetEnd');
    const targetEnd = targetEndVal ? format(new Date(targetEndVal), 'dd.MM.yyyy') : null;
    return (
        <div className={`event-card event-card--${mode}  background-color-white border-radius-0dot75rem padding-left-1dot5rem padding-top-1dot5rem padding-right-1dot5rem padding-bottom-1dot5rem shadow-large-light`}>
            <Link to={serviceUrl} data-gtm='service_card' className='event-card__link display-block'>
                {cover &&
                    <Browser>
                        <div className='event-card__cover-container position-relative'>
                            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                <>
                                    <SuspenseImageStaticSize
                                        className='max-width-100percent border-radius-0dot5rem card-shadow-small event-card__cover display-block'
                                        src={cover}
                                    />
                                    {highlightCover && <SuspenseImageStaticSize className='event-card__cover-highlight' src={cover}></SuspenseImageStaticSize>}
                                </>
                            </ErrorBoundary>
                        </div>
                    </Browser>
                }
                <div className='event-card__details'>
                    <div className='event-card__pin-block'>
                        {!!service.pin &&
                            <div className='padding-top-0dot75rem event-card__pin'>
                                <div className='display-flex'>
                                    <div className='padding-0dot25rem padding-right-0dot625rem background-color-success-50 border-radius-1rem'>
                                        <div className='display-flex align-content-center'>
                                            <div className='padding-top-0dot125rem padding-right-0dot625rem padding-bottom-0dot125rem padding-left-0dot625rem background-color-success-600 border-radius-1rem'>
                                                <div className='text-sm media color-white'><FormattedMessage defaultMessage='Top event'/></div>
                                            </div>
                                            <div className='padding-left-0dot75rem padding-top-0dot125rem padding-bottom-0dot125rem'>
                                                <div className='text-sm medium color-success-700'><FormattedMessage defaultMessage='Ottry recommends'/></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    {highlightResult ?
                        <div className='event-card__title padding-top-0dot75rem text-xl semibold color-gray-900'>
                            {i18ns(highlightResult, 'name', locale)
                                ? <Focus {...{value: i18ns(highlightResult, 'name', locale).value}}/> 
                                : i18n(service, 'name', locale)
                            }
                        </div>
                        :
                        <div className='event-card__title padding-top-0dot75rem text-xl semibold color-gray-900'>
                            {i18n(service, 'name', locale)}
                        </div>
                    }
                    {snippetResult && i18ns(snippetResult, 'details', locale) &&
                        <div className='event-card__search padding-top-0dot25rem text-sm color-gray-500 overflow-wrap-break-word'>
                            <Focus {...{value: i18ns(snippetResult, 'details', locale).value}}/>
                        </div>
                    }
                    {target &&
                        <div className='event-card__date padding-top-0dot75rem display-flex align-items-center'>
                            <CalendarSvg className='width-1dot25rem height-1dot25rem color-gray-400'/>
                            <p className='event-card__date-text text-md medium color-gray-500 margin-left-0dot5rem'>
                                {target}
                            </p>
                            {targetEnd && target !== targetEnd &&
                                <p className='event-card__date-text text-md medium color-gray-500 padding-left-0dot25rem'>{` — ${targetEnd}`}</p>
                            }
                        </div>
                    }
                    {locationName &&
                        <div className='event-card__location padding-top-0dot75rem display-flex'>
                            <MarkerPin01Svg className='flex-shrink-0 display-block width-1dot25rem height-1dot25rem color-gray-400 margin-top-0dot125rem'/>
                            <div className='event-card__location-name text-md medium color-gray-500 margin-left-0dot5rem overflow-wrap-break-word'>
                                {highlightResult && i18ns(highlightResult, 'location', locale)
                                    ? <Focus {...{value: i18ns(highlightResult, 'location', locale).value}}/> 
                                    : locationName
                                }
                            </div>
                        </div>
                    }
                </div>
            </Link>
            <div className='event-card__action padding-top-2rem'>
                <RouterLinkButton to={serviceUrl} plain='true' color='primary' iconPosition='right' gtm='service_card'>
                    <FormattedMessage defaultMessage='Buy ticket'/>
                    <ArrowUpRightSvg />
                </RouterLinkButton>
            </div>
        </div>
    );
});

export default React.memo(EventCard);